<template>
  <div class="page-header">
    <h1
      id="students-h1"
      class="h1"
    >
      Schüler - Übersicht
    </h1>
    <div
      class="actions"
      style="display:flex;align-items:center;gap:8px;"
    >
      <button
        v-if="isAdmin"
        class="btn"
        aria-label="Procurat-Liste"
        @click="openProcurat = true"
      >
        Procurat-Liste
      </button>
      <button
        v-if="isAdmin"
        class="btn"
        aria-label="Versetzen"
        @click="openPromoteDialog"
      >
        Versetzen
      </button>
      <button
        class="btn btn-primary"
        aria-labelledby="students-h1"
        @click="openCreate = true"
      >
        Neu
      </button>
    </div>
  </div>

  <div class="card">
    <!-- Filterleiste (serverseitige Suche) -->
    <div class="filters">
      <input
        v-model="q"
        placeholder="Name, Nummer oder Webschule"
        autocomplete="off"
        @keyup="doSearch"
      >
      <div
        class="field"
        style="min-width:160px;"
      >
        <label for="flt-class">Klasse</label>
        <select
          id="flt-class"
          v-model="classFilter"
          @change="doSearch"
        >
          <option value="">
            Alle
          </option>
          <option
            v-for="c in classOptions"
            :key="'flt-class-'+c.value"
            :value="c.value"
          >
            {{ c.label }}
          </option>
        </select>
      </div>
      <div
        class="field"
        style="min-width:200px;"
      >
        <label for="flt-status">Status</label>
        <select
          id="flt-status"
          v-model="statusFilter"
          @change="doSearch"
        >
          <option value="">
            Alle
          </option>
          <option
            v-for="s in statusOptions"
            :key="'flt-'+s.value"
            :value="s.value"
          >
            {{ s.label }}
          </option>
        </select>
      </div>
      <button
        class="btn btn-primary"
        :disabled="!q && !year && !statusFilter && !classFilter"
        @click="resetFilters"
      >
        Reset
      </button>
    </div>

    <!-- Tabellen-Header -->
    <div
      class="table-header"
      style="grid-template-columns: 1.3fr 1fr 160px 160px 100px 140px 100px 100px;"
    >
      <div
        class="sortable"
        role="button"
        tabindex="0"
        @click="setSort('name')"
        @keydown.enter="setSort('name')"
      >
        Name
        <span v-if="clientSortKey === 'name'">{{ clientSortDir === 'asc' ? '▲' : '▼' }}</span>
      </div>
      <div
        class="sortable"
        role="button"
        tabindex="0"
        @click="setSort('birth_date')"
        @keydown.enter="setSort('birth_date')"
      >
        Geburtsdatum
        <span v-if="clientSortKey === 'birth_date'">{{ clientSortDir === 'asc' ? '▲' : '▼' }}</span>
      </div>
      <div
        class="sortable"
        role="button"
        tabindex="0"
        @click="setSort('student_number')"
        @keydown.enter="setSort('student_number')"
      >
        Nummer
        <span v-if="clientSortKey === 'student_number'">{{ clientSortDir === 'asc' ? '▲' : '▼' }}</span>
      </div>
      <div
        class="sortable"
        role="button"
        tabindex="0"
        @click="setSort('webschule')"
        @keydown.enter="setSort('webschule')"
      >
        Webschule
        <span v-if="clientSortKey === 'webschule'">{{ clientSortDir === 'asc' ? '▲' : '▼' }}</span>
      </div>
      <div class="text-center">
        Klasse
      </div>
      <div class="text-center">
        Status
      </div>
      <div class="text-center">
        Gruppen | Eltern
      </div>
      <div class="text-right">
        Aktionen
      </div>
    </div>

    <!-- Zeilen -->
    <div
      v-for="s in sortedItems"
      :key="s.id"
      class="table-row"
      style="grid-template-columns: 1.3fr 1fr 160px 160px 100px 140px 100px 100px;"
    >
      <div><strong>{{ s.last_name }}, {{ s.first_name }}</strong></div>
      <div class="muted">
        {{ fmt(s.birth_date) }}
      </div>
      <div class="muted">
        {{ s.student_number }}
      </div>
      <div class="muted">
        {{ s.webschule }}
      </div>
      <div class="text-center muted">
        {{ s.class_level ?? '—' }}
      </div>
      <div class="text-center muted">
        {{ s.status_label ?? s.status_code ?? '—' }}
      </div>
      <div
        class="text-center muted"
        :title="groupsParentsTitle(s)"
      >
        {{ s.group_count ?? 0 }} | {{ s.parent_count ?? 0 }}
      </div>
      <div class="row-actions">
        <button
          class="icon-btn"
          :disabled="String(s.status_code ?? '').toLowerCase() !== 'aktiv'"
          :aria-disabled="String(s.status_code ?? '').toLowerCase() !== 'aktiv' ? 'true' : 'false'"
          :title="String(s.status_code ?? '').toLowerCase() === 'aktiv' ? 'Gruppen zuweisen' : 'Nur für aktive Schüler verfügbar'"
          aria-label="Gruppen zuweisen"
          @click="openAssignFor(s)"
        >
          <i
            class="pi pi-users"
            aria-hidden="true"
          />
        </button>
        <button
          class="icon-btn"
          title="Bearbeiten"
          aria-label="Bearbeiten"
          @click="edit(s)"
        >
          <i
            class="pi pi-pencil"
            aria-hidden="true"
          />
        </button>
        <button
          class="icon-btn danger"
          title="Löschen"
          aria-label="Löschen"
          @click="confirmDelete(s)"
        >
          <i
            class="pi pi-trash"
            aria-hidden="true"
          />
        </button>
      </div>
    </div>

    <p
      v-if="items.length === 0"
      class="muted"
    >
      Keine Einträge.
    </p>

    <!-- Pagination -->
    <div
      class="mt-12"
      style="display:flex;align-items:center;gap:8px;justify-content:flex-end;"
    >
      <span
        class="muted"
        style="margin-right:auto"
      >Gesamt: {{ realTotal }}</span>
      <label
        class="muted"
        for="st-limit"
      >pro Seite:</label>
      <select
        id="st-limit"
        :value="limit"
        @change="changeLimit"
      >
        <option
          v-for="n in [10,20,50,100]"
          :key="n"
          :value="n"
        >
          {{ n }}
        </option>
      </select>
      <button
        class="btn"
        :disabled="page<=1"
        @click="prevPage"
      >
        Zurück
      </button>
      <span class="muted">Seite {{ page }} / {{ pages }}</span>
      <button
        class="btn"
        :disabled="page>=pages"
        @click="nextPage"
      >
        Weiter
      </button>
    </div>
  </div>

  <BaseDialog
    :open="openCreate || !!current"
    :disabled="loading"
    @close="closeDialog"
  >
    <template #title>
      {{ current ? 'Schüler bearbeiten' : 'Neuer Schüler' }}
    </template>
    <form @submit.prevent="submit">
      <div class="form">
        <div
          class="two-col"
          style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;"
        >
          <div
            class="field"
            :class="{'has-error': !!errors.first_name}"
          >
            <label for="first">Vorname</label>
            <input
              id="first"
              v-model="form.first_name"
              autocomplete="off"
            >
            <p
              v-if="errors.first_name"
              class="help danger"
              role="alert"
            >
              {{ errors.first_name }}
            </p>
          </div>
          <div
            class="field"
            :class="{'has-error': !!errors.last_name}"
          >
            <label for="last">Nachname</label>
            <input
              id="last"
              v-model="form.last_name"
              autocomplete="off"
            >
            <p
              v-if="errors.last_name"
              class="help danger"
              role="alert"
            >
              {{ errors.last_name }}
            </p>
          </div>
          <div
            class="field"
            :class="{'has-error': !!errors.birth_date}"
          >
            <label for="birth">Geburtsdatum</label>
            <Calendar
              id="birth"
              v-model="birthAt"
              :manual-input="false"
              date-format="dd.mm.yy"
              :show-icon="true"
              :touch-u-i="true"
            />
            <p
              v-if="errors.birth_date"
              class="help danger"
              role="alert"
            >
              {{ errors.birth_date }}
            </p>
          </div>
        </div>

        <div
          class="two-col"
          style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;"
        >
          <div
            class="field"
            :class="{'has-error': !!errors.enrollment_year}"
          >
            <label for="enroll">Einschulungsjahr</label>
            <select
              id="enroll"
              v-model.number="form.enrollment_year"
            >
              <option
                :value="''"
                disabled
              >
                Bitte wählen
              </option>
              <option
                v-for="o in yearsOptions"
                :key="o.value"
                :value="o.value"
              >
                {{ o.label }}
              </option>
            </select>
            <p
              v-if="errors.enrollment_year"
              class="help danger"
              role="alert"
            >
              {{ errors.enrollment_year }}
            </p>
          </div>
          <div
            class="field"
            :class="{'has-error': !!errors.class_level}"
          >
            <label for="currentClass">Klasse</label>
            <select
              id="currentClass"
              v-model.number="form.class_level"
            >
              <option
                :value="''"
                disabled
              >
                Bitte wählen
              </option>
              <option
                v-for="c in classOptions"
                :key="'class_level-'+c.value"
                :value="c.value"
              >
                {{ c.label }}
              </option>
            </select>
            <p
              v-if="errors.class_level"
              class="help danger"
              role="alert"
            >
              {{ errors.class_level }}
            </p>
          </div>
        </div>

        <div
          class="two-col"
          style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;"
        >
          <div
            class="field"
            :class="{'has-error': !!errors.student_number}"
          >
            <label for="num">Schülernummer</label>
            <input
              id="num"
              v-model="form.student_number"
              autocomplete="off"
            >
            <p
              v-if="errors.student_number"
              class="help danger"
              role="alert"
            >
              {{ errors.student_number }}
            </p>
          </div>
          <div
            class="field"
            :class="{'has-error': !!errors.webschule}"
          >
            <label for="webschule">Webschule</label>
            <input
              id="webschule"
              v-model="form.webschule"
              autocomplete="off"
            >
            <p
              v-if="errors.webschule"
              class="help danger"
              role="alert"
            >
              {{ errors.webschule }}
            </p>
          </div>
          <div
            class="field"
            :class="{'has-error': !!errors.status_id}"
          >
            <label for="status">Status</label>
            <select
              id="status"
              v-model.number="form.status_id"
            >
              <option
                :value="''"
                disabled
              >
                Bitte wählen
              </option>
              <option
                v-for="s in statusOptions"
                :key="s.value"
                :value="s.value"
              >
                {{ s.label }}
              </option>
            </select>
            <p
              v-if="errors.status_id"
              class="help danger"
              role="alert"
            >
              {{ errors.status_id }}
            </p>
          </div>
        </div>
      </div>
      <p
        v-if="errorMsg"
        class="notice notice-danger mt-12"
        role="alert"
      >
        {{ errorMsg }}
      </p>
      <div
        class="mt-12"
        style="display:flex;gap:8px;justify-content:flex-end;"
      >
        <button
          type="button"
          class="btn"
          :disabled="loading"
          @click="closeDialog"
        >
          Abbrechen
        </button>
        <button
          type="submit"
          class="btn btn-primary"
          :disabled="loading"
        >
          <span
            v-if="loading"
            class="spinner"
            aria-hidden="true"
          /> Speichern
        </button>
      </div>
    </form>
  </BaseDialog>

  <BaseDialog
    :open="!!toDelete"
    :disabled="loading"
    @close="toDelete=null"
  >
    <template #title>
      Schüler löschen?
    </template>
    <p>Diese Aktion kann nicht rückgängig gemacht werden.</p>
    <div
      class="mt-12"
      style="display:flex;gap:8px;justify-content:flex-end;"
    >
      <button
        class="btn"
        :disabled="loading"
        @click="toDelete=null"
      >
        Abbrechen
      </button>
      <button
        class="btn btn-warn"
        :disabled="loading"
        @click="doDelete"
      >
        <span
          v-if="loading"
          class="spinner"
          aria-hidden="true"
        /> Löschen
      </button>
    </div>
  </BaseDialog>

  <!-- Versetzungsdialog -->
  <PromoteStudentsDialog
    :open="openPromote"
    :disabled="loading"
    :items="promotionItems"
    :info="promotionInfo"
    :school-years="schoolYears"
    @close="openPromote=false"
    @applied="onPromotionsApplied"
  />

  <!-- Zuweisungsdialog: Schüler → Gruppen -->
  <AssignDialog
    :open="openAssign"
    :student="selectedStudent"
    @close="() => { openAssign=false; selectedStudent=null; }"
    @applied="onAssignApplied"
  />

  <!-- Procurat-Abgleich -->
  <ProcuratDialog
    :visible="openProcurat"
    @close="openProcurat=false"
    @applied="onProcuratApplied"
  />

  <!-- Top-Navigation: Hilfe-Button -->
  <teleport to=".top-nav">
    <span
      id="top-nav-help-students"
      class="top-nav-help"
    >
      <UiInfoButton title="Hilfe: Schülerliste">
        <ul>
          <li>Filter: Suche (Name/Nummer/Webschule), Status sowie Klasse; Änderungen laden automatisch.</li>
          <li>Spalten: Name, Geburtsdatum, Nummer, Webschule, Klasse, Status, Anzahl Gruppen und Eltern.</li>
          <li>Aktionen: Gruppen zuweisen (nur für aktive Schüler), Bearbeiten, Löschen.</li>
          <li>Dialog „Neu/Bearbeiten“: Stammdaten inkl. Datumspicker und Schuljahr; Pflichtfelder sind markiert.</li>
          <li>Admin: „Versetzen“ ermöglicht Sammelversetzung ins nächste Schuljahr.</li>
        </ul>
      </UiInfoButton>
    </span>
  </teleport>
</template>

<script setup>
import {computed, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {getUser} from '@/services/authStorage';
import api from '@/services/api';
import {useToast} from 'primevue/usetoast';
import BaseDialog from '@/components/BaseDialog.vue';
import AssignDialog from '@/components/AssignDialog.vue';
import UiInfoButton from '@/components/UiInfoButton.vue';
import PromoteStudentsDialog from '@/components/PromoteStudentsDialog.vue';
import ProcuratDialog from '@/components/ProcuratDialog.vue';

const items = ref([]);
const loading = ref(false);
const openCreate = ref(false);
const openPromote = ref(false);
const openAssign = ref(false);
const openProcurat = ref(false);
const selectedStudent = ref(null);
const promotionItems = ref([]);
const promotionInfo = ref(null);

// Auswahl/Ziel-Logik in PromoteStudentsDialog verschoben

// Auswahl-Toggle wird nun in PromoteStudentsDialog gehandhabt

// „Alle auswählen“ wird nun in PromoteStudentsDialog gehandhabt

// Zielvorschlag und Ziel-Optionen sind nun Bestandteil von PromoteStudentsDialog

const current = ref(null);
const toDelete = ref(null);
const toast = useToast();

const currentUser = getUser();
const isAdmin = computed(() => currentUser?.roleId === 1);

// Pagination + Filter
const page = ref(1);
const limit = ref(20);
const total = ref(0);
const pages = ref(0);
const realTotal = ref(0);
const q = ref('');
const year = ref('');
const statusFilter = ref('');
// NEU: Klassen-Filter (z. B. Klassenstufe 1–10)
const classFilter = ref('');

// Einfache statische Klassen-Optionen 1–10
const classOptions = computed(() => {
  return Array.from({length: 10}, (_, i) => {
    const lvl = i + 1;
    return {value: lvl, label: lvl};
  });
});

const sortServer = ref('');   // 'status' oder leer
const dirServer = ref('asc');

const form = ref({
  first_name: '',
  last_name: '',
  birth_date: '',
  enrollment_year: '',
  student_number: '',
  class_level: '',
  webschule: '',
  status_id: ''
});
const errors = ref({});
const errorMsg = ref('');

// Schuljahre (Dropdown)
const schoolYears = ref([]);
const yearsOptions = computed(() => {
  const parseStartYear = (code) => {
    const m = String(code ?? '').match(/^(\d{4})/);
    return m ? Number(m[1]) : -1;
  };

  return schoolYears.value
      .map(sy => {
        const label = sy.code ?? sy.school_year ?? sy.name ?? sy.label ?? sy.year ?? '';
        const rawId = sy.id ?? sy.school_year_id ?? null;
        const id = rawId != null ? Number(rawId) : null;
        return id != null ? {value: id, label} : null;
      })
      .filter(Boolean)
      .sort((a, b) => parseStartYear(b.label) - parseStartYear(a.label)); // DESC
});

// Schuljahre laden – vollständig, DESC (auch für sehr alte Jahrgänge)
async function loadSchoolYears(force = false) {
  if (!force && schoolYears.value.length > 0) {
    return;
  }
  try {
    const {data} = await api.get('/school-years', {
      params: {
        page: 1,
        limit: 500,
        sort: 'code',
        dir: 'desc',
      }
    });

    schoolYears.value = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
            ? data
            : []; // kein slice/limit im UI
  } catch (e) {
    schoolYears.value = [];
  }
}

// Aktuelles Schuljahr ermitteln (heute in [start_date, end_date])
function getCurrentSchoolYear() {
  const today = new Date();
  const iso = (d) => typeof d === 'string' ? d : '';
  const cur = schoolYears.value.find(sy => {
    const s = new Date(iso(sy.start_date) + 'T00:00:00');
    const e = new Date(iso(sy.end_date) + 'T23:59:59');
    return today >= s && today <= e;
  }) || schoolYears.value[0] || null; // Fallback: neuestes
  return cur;
}

// enrollmentLabel wird innerhalb von PromoteStudentsDialog bereitgestellt

async function openPromoteDialog() {
  if (!isAdmin.value) return;
  loading.value = true;
  try {
    await loadSchoolYears(true);
    const cur = getCurrentSchoolYear();
    if (!cur) {
      promotionItems.value = [];
      promotionInfo.value = null;
      openPromote.value = true;
      return;
    }
    const {data} = await api.get('/students/promotions', {params: {school_year_id: cur.id}});
    promotionItems.value = Array.isArray(data?.items) ? data.items : [];
    promotionInfo.value = {schoolYearId: cur.id, schoolYearLabel: cur.code ?? ''};

    openPromote.value = true;
  } finally {
    loading.value = false;
  }
}

// Versetzungsanwendung erfolgt nun in PromoteStudentsDialog

// Hilfsfunktion: ID eines Schuljahres aus dessen Code bestimmen
function getSchoolYearIdByCode(code) {
  if (!code) return null;
  const hit = schoolYears.value.find(sy => (sy.code ?? sy.school_year) === code);
  return hit?.id ?? null;
}

function groupsParentsTitle(s) {
  const parts = [];

  // Erwartete Struktur (Beispiele):
  // s.groups  = [{ name: 'Mathe AG' }, { name: 'Chor' }, ...]
  // s.parents = [{ name: 'Müller, Anna', relation: 'Mutter' }, ...]
  const groups = Array.isArray(s.groups) ? s.groups : [];
  const parents = Array.isArray(s.parents) ? s.parents : [];

  if (groups.length) {
    const groupLabels = groups
        .map(g => (g.name || g.label || '').toString().trim())
        .filter(Boolean);
    if (groupLabels.length) {
      parts.push('Gruppen: ' + groupLabels.join(', '));
    }
  }

  if (parents.length) {
    const parentLabels = parents
        .map(p => {
          const n = (p.name || `${p.last_name ?? ''} ${p.first_name ?? ''}`).toString().trim();
          const rel = (p.relationship || p.relation || '').toString().trim();
          if (!n) return '';
          return rel ? `${n} (${rel})` : n;
        })
        .filter(Boolean);
    if (parentLabels.length) {
      parts.push('Eltern: ' + parentLabels.join(', '));
    }
  }

  // Wenn nichts da ist, kein Tooltip
  return parts.join('\n') || '';
}

function fmt(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

function formatDe(d) {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

// Date-Objekt für PrimeVue Calendar (muss VOR watchers stehen)
const birthAt = ref(null);

// Clientseitige Sortierung (Default: Name aufsteigend)
const clientSortKey = ref('name'); // 'name' | 'birth_date' | 'student_number'
const clientSortDir = ref('asc');  // 'asc' | 'desc'

const sortedItems = computed(() => {
  const arr = [...items.value];
  const getVal = (s) => {
    switch (clientSortKey.value) {
      case 'birth_date': {
        const iso = s.birth_date || '';
        const t = iso ? Date.parse(iso) : NaN;
        return Number.isNaN(t) ? 0 : t;
      }
      case 'student_number': {
        const raw = s.student_number ?? '';
        const num = Number(raw);
        return Number.isNaN(num) ? String(raw).toLowerCase() : num;
      }
      case 'name':
      default: {
        const last = (s.last_name || '').trim().toLowerCase();
        const first = (s.first_name || '').trim().toLowerCase();
        return `${last}, ${first}`;
      }
    }
  };
  arr.sort((a, b) => {
    const va = getVal(a);
    const vb = getVal(b);
    let result;
    if (typeof va === 'number' && typeof vb === 'number') {
      result = va - vb;
    } else {
      result = String(va).localeCompare(String(vb), 'de', {sensitivity: 'base'});
    }
    return clientSortDir.value === 'asc' ? result : -result;
  });
  return arr;
});

function setSort(key) {
  if (clientSortKey.value === key) {
    clientSortDir.value = clientSortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    clientSortKey.value = key;
    clientSortDir.value = 'asc';
  }
}

// beim Kalender-Change Formfeld setzen
watch(birthAt, () => {
  form.value.birth_date = birthAt.value instanceof Date ? formatDe(birthAt.value) : '';
});

// Beim Öffnen von "Neu" (Create) sicherstellen, dass Schuljahre geladen sind
watch(openCreate, (val) => {
  if (val) {
    loadSchoolYears();
  }
});

// Status (Dropdown)
const statuses = ref([]);
const statusOptions = computed(() => {
  return statuses.value.map(s => ({value: Number(s.id), label: s.label ?? s.code}));
});

async function loadStatuses(force = false) {
  if (!force && statuses.value.length > 0) return;
  try {
    const {data} = await api.get('/students/statuses');
    const list = Array.isArray(data?.items) ? data.items : [];
    statuses.value = list;
  } catch (e) {
    statuses.value = [];
  }
}

// Beim Öffnen von "Neu" (Create) sicherstellen, dass Status geladen ist und "aktiv" vorauswählen
watch(openCreate, async (val) => {
  if (val) {
    await loadStatuses();
    if (!current.value) {
      const act = statuses.value.find(s => String(s.code).toLowerCase() === 'aktiv');
      if (act) {
        form.value.status_id = Number(act.id);
      }
    }
  }
});

// Beim Öffnen von "Bearbeiten" (Edit) Status laden
watch(current, (val) => {
  if (val) {
    loadStatuses();
  }
});

// Sobald Schuljahre geladen sind: fehlende ID aus Row/Code ableiten (nur bei Edit)
watch(schoolYears, () => {
  if (current.value) {
    if (form.value.enrollment_year !== '' && form.value.enrollment_year !== null && form.value.enrollment_year !== undefined) {
      form.value.enrollment_year = Number(form.value.enrollment_year);
      return;
    }
    const idFromEnrollment = current.value.enrollment_year != null ? Number(current.value.enrollment_year) : null;
    if (idFromEnrollment != null) {
      form.value.enrollment_year = idFromEnrollment;
      return;
    }
    const idFromRow = current.value.school_year_id != null ? Number(current.value.school_year_id) : null;
    if (idFromRow != null) {
      form.value.enrollment_year = idFromRow;
      return;
    }
    const code = current.value.school_year_code ?? '';
    if (code) {
      const id = getSchoolYearIdByCode(code);
      if (id) form.value.enrollment_year = Number(id);
    }
  }
});

async function load() {
  loading.value = true;
  const {data} = await api.get('/students', {
    params: {
      page: page.value,
      limit: limit.value,
      q: q.value || undefined,
      year: year.value || undefined,
      // NEU: Klassenfilter an das Backend durchreichen
      class: classFilter.value || undefined,
      status: statusFilter.value || undefined,
      sort: sortServer.value || undefined,
      dir: dirServer.value || undefined
    }
  });
  items.value = data.items || [];
  total.value = data.total || 0;
  pages.value = data.pages || 0;
  realTotal.value = data.realTotal;
  loading.value = false;
}

function nextPage() {
  if (page.value < pages.value) {
    page.value++;
    load();
  }
}

function prevPage() {
  if (page.value > 1) {
    page.value--;
    load();
  }
}

function changeLimit(e) {
  limit.value = Number(e.target.value);
  page.value = 1;
  load();
}

function onAssignApplied() {
  // Liste aktualisieren, kein zusätzlicher Toast (kommt aus dem Dialog)
  load();
}

function onPromotionsApplied() {
  openPromote.value = false;
  load();
}

function onProcuratApplied() {
  openProcurat.value = false;
  load();
}

function doSearch() {
  page.value = 1;
  load();
}

function openAssignFor(row) {
  selectedStudent.value = row;
  openAssign.value = true;
}

function edit(row) {
  current.value = row;
  birthAt.value = row?.birth_date ? new Date(row.birth_date + 'T00:00:00') : null;

  const idByEnrollment = row.enrollment_year != null ? Number(row.enrollment_year) : null;
  const idByRow = row.school_year_id != null ? Number(row.school_year_id) : null;
  const idByCode = row.school_year_code ? getSchoolYearIdByCode(row.school_year_code) : null;

  const schoolYearId = idByEnrollment ?? idByRow ?? (idByCode != null ? Number(idByCode) : null);

  form.value = {
    first_name: row.first_name,
    last_name: row.last_name,
    birth_date: fmt(row.birth_date),
    enrollment_year: schoolYearId,
    student_number: row.student_number,
    class_level: row.class_level ?? '',
    webschule: row.webschule ?? '',
    status_id: row.status_id ?? ''
  };
}

function closeDialog() {
  openCreate.value = false;
  current.value = null;
  birthAt.value = null;
  form.value = {
    first_name: '',
    last_name: '',
    birth_date: '',
    enrollment_year: '',
    student_number: '',
    class_level: '',
    webschule: '',
    status_id: ''
  };
  errors.value = {};
  errorMsg.value = '';
}

async function submit() {
  loading.value = true;
  errors.value = {};
  errorMsg.value = '';

  try {
    const payload = {
      first_name: (form.value.first_name ?? '').trim(),
      last_name: (form.value.last_name ?? '').trim(),
      birth_date: form.value.birth_date ?? '',
      student_number: (form.value.student_number ?? '').trim(),
      webschule: (form.value.webschule ?? '').trim(),
      enrollment_year: form.value.enrollment_year !== '' && form.value.enrollment_year !== null && form.value.enrollment_year !== undefined
          ? Number(form.value.enrollment_year)
          : undefined,
      class_level: form.value.class_level !== '' && form.value.class_level !== null && form.value.class_level !== undefined
          ? Number(form.value.class_level)
          : undefined,
      status_id: form.value.status_id !== '' && form.value.status_id !== null && form.value.status_id !== undefined
          ? Number(form.value.status_id)
          : undefined
    };

    if (current.value) {
      await api.put(`/students/${current.value.id}`, payload);
    } else {
      await api.post('/students', payload);
    }
    closeDialog();
    await load();
    toast.add({severity: 'success', summary: 'Gespeichert.', life: 3500});
  } catch (e) {
    const res = e.response?.data;
    if (res?.error?.details?.fields) {
      errors.value = res.error.details.fields;
    } else {
      errorMsg.value = res?.error?.message || 'Aktion fehlgeschlagen.';
    }
  } finally {
    loading.value = false;
  }
}

function confirmDelete(row) {
  toDelete.value = row;
}

async function doDelete() {
  loading.value = true;
  try {
    await api.delete(`/students/${toDelete.value.id}`);
    items.value = items.value.filter(i => i.id !== toDelete.value.id);
    toDelete.value = null;
    toast.add({severity: 'success', summary: 'Gelöscht.', life: 3500});
  } catch (e) {
    const res = e?.response?.data;
    const code = res?.code || res?.error?.code;
    const message = res?.message || res?.error?.message || '';

    if (code === '23503' || /foreign key violation/i.test(message) || /violates foreign key constraint/i.test(message)) {
      toast.add({
        severity: 'danger',
        summary: 'Löschen nicht möglich',
        detail: 'Der Schüler ist noch mit anderen Daten verknüpft und kann nicht gelöscht werden.',
        life: 5500
      });
    } else {
      toast.add({
        severity: 'danger',
        summary: 'Löschen fehlgeschlagen.',
        detail: message || 'Bitte versuchen Sie es später erneut.',
        life: 4000
      });
    }
  } finally {
    loading.value = false;
  }
}

function resetFilters() {
  q.value = '';
  year.value = '';
  classFilter.value = '';
  const act = statuses.value.find(s => String(s.code).toLowerCase() === 'aktiv');
  statusFilter.value = act ? Number(act.id) : '';
  sortServer.value = '';
  dirServer.value = 'asc';
  page.value = 1;
  load();
}

onMounted(async () => {
  await loadStatuses();
  if (!statusFilter.value) {
    const act = statuses.value.find(s => String(s.code).toLowerCase() === 'aktiv');
    if (act) statusFilter.value = Number(act.id);
  }
  await load();
  loadSchoolYears();
});

// Hilfe-Button vor .profile einsortieren
onMounted(() => {
  const nav = document.querySelector('.top-nav');
  const el = document.getElementById('top-nav-help-students');
  if (!nav || !el) return;
  const profile = nav.querySelector('.profile');
  if (profile && profile.parentNode === nav) nav.insertBefore(el, profile);
  else nav.appendChild(el);
});

onBeforeUnmount(() => {
  const el = document.getElementById('top-nav-help-students');
  if (el && el.parentNode) el.parentNode.removeChild(el);
});
</script>

<style scoped>
.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  text-decoration: underline;
}

.table-row {
  padding: 5px 0;
  font-size: 14px;
}

/* Fehlerzustand für Felder mit Validierungsfehlern */
.field.has-error :deep(input),
.field.has-error :deep(select),
.field.has-error :deep(textarea) {
  border-color: var(--danger);
}

.field.has-error :deep(.p-inputtext),
.field.has-error :deep(.p-calendar .p-inputtext) {
  border-color: var(--danger);
}

.field.has-error p.help {
  font-size: 12px;
}
</style>